import SignUpForm from "../../components/auth/SignUpForm";
import { env } from "../../env";
import useAuth from "../../hooks/useAuth";
import logoBlack from "../../vendor/logo_black.svg";
import logoWhite from "../../vendor/logo_white.svg";
import { Alert as MuiAlert, Paper, Typography } from "@mui/material";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { spacing } from "@mui/system";
import { useCurrentPoliciesQuery, useSignUpMutation } from "generated.hooks";
import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components/macro";
import { safeReturnTo } from "utils/returnTo";

const Alert = styled(MuiAlert)(spacing);

const Brand = styled("img")`
  width: 250px;
  margin-bottom: 32px;
`;

const Wrapper = styled(Paper)`
  padding: ${(props) => props.theme.spacing(6)};

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: ${(props) => props.theme.spacing(10)};
  }
`;

function SignIn() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Where the visitor was headed, such as a team invite: kept through sign-up
  // so a new account lands back on it rather than on the home page.
  const returnTo = safeReturnTo(searchParams.get("returnTo"));
  const [signUp, { loading, error }] = useSignUpMutation();
  // Owned here and passed down, so the form's rendering decision and the
  // versions submitted always come from the same result. The generated hooks
  // default to `no-cache`, so a second query in the form would be an
  // independent request that could resolve at a different time.
  const { data: policies } = useCurrentPoliciesQuery();
  const publishedPolicies = policies?.currentPolicies ?? [];
  const { signIn } = useAuth();
  const theme = useMuiTheme();
  const brandSrc = theme.palette.mode === "dark" ? logoWhite : logoBlack;
  const turnstileEnabled = Boolean(env.REACT_APP_TURNSTILE_SITE_KEY);

  return (
    <React.Fragment>
      <Brand alt="Podium" src={brandSrc} />
      <Wrapper>
        <Helmet title="Sign Up" />
        <Typography component="h2" variant="body1" align="center">
          Create a new account
        </Typography>
        {error && (
          <Alert mt={2} mb={3} severity="warning">
            Internal server error
          </Alert>
        )}
        <SignUpForm
          initialValues={{
            accountName: "",
            firstName: "",
            lastName: "",
            email: "",
            password1: "",
            password2: "",
            turnstileToken: "",
            acceptsPolicies: false,
            ageConfirmed: false,
            // Pre-ticked: news and product updates are on unless the person
            // unticks the box. They can change it later in Settings, and
            // every email carries an unsubscribe link.
            marketingOptIn: true,
          }}
          onSubmit={(data, { setErrors, setFieldValue, setSubmitting }) => {
            // The single "I agree" control covers both documents; the consent
            // record is written per document, against the exact versions the
            // form displayed.
            const { acceptsPolicies, ...rest } = data as typeof data & {
              acceptsPolicies?: boolean;
            };
            return signUp({
              variables: {
                ...rest,
                acceptedPolicies: acceptsPolicies
                  ? publishedPolicies.map((policy) => ({
                      documentType: policy.documentType,
                      version: policy.version,
                    }))
                  : [],
              },
            })
              .then(async (res) => {
                if (res.data?.registerWithAccount.success) {
                  if (turnstileEnabled) {
                    navigate(
                      returnTo === "/"
                        ? "/auth/sign-in"
                        : `/auth/sign-in?returnTo=${encodeURIComponent(
                            returnTo
                          )}`
                    );
                  } else {
                    await (signIn as any)(data.email, data.password1);
                    navigate(returnTo);
                  }
                } else {
                  setFieldValue("turnstileToken", "", false);
                  setErrors(
                    Object.keys(res.data?.registerWithAccount.errors).reduce(
                      (result, field) => {
                        result[field] =
                          res.data?.registerWithAccount.errors[
                            field
                          ][0].message;
                        return result;
                      },
                      {}
                    )
                  );
                }
              })
              .catch(() => {
                setFieldValue("turnstileToken", "", false);
              })
              .finally(() => {
                setSubmitting(false);
              });
          }}
          loading={loading}
          policies={publishedPolicies}
        />
      </Wrapper>
    </React.Fragment>
  );
}

export default SignIn;
